import type { DropboxResponseError, files } from "@dropbox/api-v2-client";
import type {
  FssOpsToolCall,
  FssOpsToolCallCopyFiles,
  FssOpsToolCallCreateFolder,
  FssOpsToolCallDeleteFiles,
  FssOpsToolCallMoveFiles,
  FssOpsToolCallRenameFile,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ToolCalls/fss/types";
import { callApiV2 } from "@mirage/service-dbx-api";
import { tagged } from "@mirage/service-logging";

const logger = tagged("FssToolCallsRunner");

export function runFssToolCall(toolCall: FssOpsToolCall): Promise<string> {
  switch (toolCall.name) {
    case "create_folder":
      return runCreateFolder(toolCall);
    case "move_files":
      return runMoveFiles(toolCall);
    case "rename_file":
      return runRenameFile(toolCall);
    case "copy_files":
      return runCopyFiles(toolCall);
    case "delete_files":
      return runDeleteFiles(toolCall);
    default:
      toolCall satisfies never;
      throw new Error(
        `Unknown tool call type: ${(toolCall as FssOpsToolCall).name}`,
      );
  }
}

async function runCreateFolder(
  toolCall: FssOpsToolCallCreateFolder,
): Promise<string> {
  const args = toolCall.arguments;
  logger.info("runCreateFolder", args);
  try {
    const result = await callApiV2("filesCreateFolder", {
      path: args.folder_path,
    });
    return `Folder created at path: ${result.path_lower}`;
  } catch (error) {
    if (error instanceof Error && isCreateFolderError(error)) {
      if (error.error.error.path[".tag"] === "conflict") {
        const conflictError = error.error.error.path.conflict;
        if (conflictError[".tag"] === "folder") {
          // folder already exists, we can just continue
          return `Folder already exists at path: ${args.folder_path}`;
        }
      }
    }
    throw error;
  }
}

function isCreateFolderError(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  e: any,
): e is { error: DropboxResponseError<files.CreateFolderError> } {
  return e.error?.error?.[".tag"] === "path";
}

async function runMoveFiles(
  toolCall: FssOpsToolCallMoveFiles,
): Promise<string> {
  const args = toolCall.arguments;
  logger.info("runMoveFiles", args);
  for (const item of args.source_paths) {
    const fileName = item.split("/").pop();
    await callApiV2("filesMove", {
      from_path: item,
      to_path: `${cleanFolderPath(args.destination_folder_path)}/${fileName}`,
    });
  }
  return `Files moved to path: ${args.destination_folder_path}`;
}

async function runRenameFile(
  toolCall: FssOpsToolCallRenameFile,
): Promise<string> {
  const args = toolCall.arguments;
  logger.info("runRenameFile", args);
  await callApiV2("filesMove", {
    from_path: args.source_path,
    to_path: args.new_path,
  });
  return `File renamed from ${args.source_path} to ${args.new_path}`;
}

async function runCopyFiles(
  toolCall: FssOpsToolCallCopyFiles,
): Promise<string> {
  const args = toolCall.arguments;
  logger.info("runCopyFiles", args);
  for (const item of args.source_paths) {
    const fileName = item.split("/").pop();
    await callApiV2("filesCopy", {
      from_path: item,
      to_path: `${cleanFolderPath(args.destination_folder_path)}/${fileName}`,
    });
  }
  return `Files copied to path: ${args.destination_folder_path}`;
}

async function runDeleteFiles(
  toolCall: FssOpsToolCallDeleteFiles,
): Promise<string> {
  const args = toolCall.arguments;
  logger.info("runDeleteFiles", args);
  for (const item of args.file_paths) {
    await callApiV2("filesDelete", {
      path: item,
    });
  }
  return `Files deleted: ${args.file_paths.join(", ")}`;
}

/**
 * Remove trailing slash from a folder path
 */
function cleanFolderPath(path: string): string {
  return path.replace(/\/$/, "");
}

export function cancelFssToolCall(toolCall: FssOpsToolCall): string {
  switch (toolCall.name) {
    case "create_folder":
      return `User cancelled the create_folder operation`;
    case "move_files":
      return `User cancelled the move_files operation`;
    case "rename_file":
      return `User cancelled the rename_file operation`;
    case "copy_files":
      return `User cancelled the copy_files operation`;
    case "delete_files":
      return `User cancelled the delete_files operation`;
    default:
      toolCall satisfies never;
      throw new Error(
        `Unknown tool call type: ${(toolCall as FssOpsToolCall).name}`,
      );
  }
}
